// Состав меню CRM: группы, разделы и ссылки на старые порталы.
//
// ПОЧЕМУ ОТДЕЛЬНЫМ ФАЙЛОМ. Разделов было 13, когда файл заводился, — сейчас их за
// четыре десятка, и это ровно тот случай, ради которого он и появился. Числа здесь
// нарочно нет: комментарий с числом устаревает молча (этот, например, отстал втрое).
// Сколько пунктов сейчас — считает nav-routes.test.tsx, он же и покраснеет, если
// пункт добавили, а маршрут забыли. Плоский список из
// сорока строк нечитаем: глазу не за что зацепиться, и «Счета на оплату» ищут
// среди «Счетов клиенту» по буквам. Поэтому меню сгруппировано по смыслу, а
// состав групп лежит данными — добавить раздел значит дописать одну строку, не
// трогая разметку.
//
// ГРУППЫ СОБРАНЫ ПО ВОПРОСУ, А НЕ ПО УСТРОЙСТВУ ЯДРА. «Счета клиенту» и «Счета
// на оплату» технически один экран смарт-процессов, но первый — про то, что
// должны нам, второй — про то, что должны мы. В меню они в «Деньгах», а не в
// «смарт-процессах»: человек ищет ответ, а не тип записи Bitrix.
//
// ГРУППА «ПОКА НА ПОРТАЛЕ» — самая важная для приживания CRM. Половина работы
// людей сегодня на четырёх самодельных порталах, и они не должны угадывать, где
// что лежит. Ссылки внешние, помечены и открываются в новой вкладке.

/** Раздел CRM. */
interface CrmItem {
  kind: 'crm'
  to: string
  label: string
  /** Чем раздел отвечает. Идёт в подсказку при наведении. */
  hint?: string
  /**
   * Экран написан, но маршрута для него в App.tsx ещё нет. Такой пункт не
   * рисуем: мёртвая ссылка на «страницу не найдена» хуже отсутствующей.
   * Появился маршрут — снять флаг, и пункт встанет на место.
   */
  pending?: boolean
  /**
   * Код раздела из прав доступа (`rights.РАЗДЕЛЫ` в web/bff/rights.py, он же приходит
   * в `/api/me` → `rights.sections[].code` и пишется в журнал доступа).
   *
   * Зачем он здесь, а не сопоставляется по адресу. `/api/me` присылает `denied_sections`
   * КОДАМИ, а путь знает только про разрешённые разделы: сопоставлять «invoices» с
   * «/smart/invoices» пришлось бы второй таблицей путей — копией серверной, которая
   * разъедется с ней при первом переезде адреса. Код рядом с пунктом меню — одна строка,
   * и она видна тому, кто пункт добавляет.
   *
   * Пункта без кода права не касаются вовсе (кабинет руководителя — это обзор поверх
   * разделов, своего кода в правах у него нет). Так и должно быть: неизвестный раздел
   * мы показываем, а не прячем — «спрятал по догадке» читается как «раздела не существует».
   */
  section?: string
}

/** Ссылка на старый портал: раздела в CRM ещё нет. */
interface PortalItem {
  kind: 'portal'
  href: string
  label: string
  /** Какой портал: люди называют их по номеру домена. */
  portal: string
  /** Чем ссылка отличается от прямого перехода в раздел. */
  hint?: string
}

export type NavItem = CrmItem | PortalItem

export interface NavGroup {
  /** Ключ для запоминания «свёрнута/развёрнута». Менять нельзя: сбросит выбор людям. */
  id: string
  title: string
  /** Пояснение под группой. Нужно там, где ссылки ведут наружу. */
  note?: string
  /** Свёрнута при самом первом заходе (дальше — как оставил пользователь). */
  collapsedByDefault?: boolean
  items: NavItem[]
}

/**
 * Меню целиком. Порядок групп — по частоте работы: продавец живёт в «Продажах»
 * и «Деньгах», руководитель заходит в свою группу раз в день, «Пока на портале»
 * внизу, потому что это временная витрина, а не часть CRM.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'sales',
    title: 'Продажи',
    items: [
      { kind: 'crm', to: '/deals', section: 'deals', label: 'Сделки', hint: 'Что у нас в работе по клиенту и стадии' },
      { kind: 'crm', to: '/companies', section: 'companies', label: 'Компании', hint: 'Найти клиента и открыть карточку' },
      { kind: 'crm', to: '/clients', section: 'clients', label: 'Клиент 360°', hint: 'Всё про одного клиента на одном экране' },
      { kind: 'crm', to: '/contacts', section: 'contacts', label: 'Контакты', hint: 'Найти человека и его компанию' },
      { kind: 'crm', to: '/vessels', section: 'vessels', label: 'Суда', hint: 'Найти судно по названию или IMO' },
      { kind: 'crm', to: '/vessel-merge', section: 'vessel-merge', label: 'Сведение судов', hint: 'Карточки одного судна, заведённые дважды: что перевесить и что потеряется' },
      { kind: 'crm', to: '/cards', section: 'cards', label: 'Карточки', hint: 'Те же компании, суда и люди, но из своей базы, а не из зеркала Bitrix' },
      // Кода раздела нет намеренно: путь сделки собирается из «Сделок», документов и 1С,
      // и права проверяются там же — маршрут /api/chain спрашивает раздел 'deals'.
      { kind: 'crm', to: '/chain', label: 'Путь сделки', hint: 'От заявки до оплаты: где работа встала и по каким сделкам чаще' },
      // Кода раздела нет намеренно: расхождения состава читаются правами «Сделок» —
      // маршрут /api/products спрашивает раздел 'deals'.
      { kind: 'crm', to: '/mismatches', label: 'Расхождения состава', hint: 'Где состав сделки не сходится с её суммой и что из этого настоящая ошибка' },
      { kind: 'crm', to: '/bp-tasks', section: 'bp-tasks', label: 'Задания процессов', hint: 'Чьё задание висит, сколько висит и что кого ждёт' },
      { kind: 'crm', to: '/bpe-tasks', section: 'bpe-tasks', label: 'Задания движка', hint: 'Задания своего движка процессов: что на вас, к какому сроку и что кого ждёт' },
      { kind: 'crm', to: '/notify-queue', section: 'notify-queue', label: 'Очередь уведомлений', hint: 'Что движок отправил людям, что ждёт отправки и что застряло — и почему' },
      { kind: 'crm', to: '/tasks', section: 'tasks', label: 'Задачи', hint: 'Задачи Bitrix из зеркала: что на ком, к какому сроку, комментарии и свод по людям' },
      { kind: 'crm', to: '/leads', section: 'leads', label: 'Потенциальные клиенты', hint: 'Лиды: кто обратился, откуда пришёл и чем кончилось — с 2025 года' },
    ],
  },
  {
    id: 'money',
    title: 'Деньги',
    items: [
      { kind: 'crm', to: '/receivables', section: 'receivables', label: 'Дебиторка', hint: 'Кто и сколько нам должен, что просрочено' },
      { kind: 'crm', to: '/bank', section: 'bank', label: 'Банк и бухгалтерия', hint: 'Выписка расчётного счёта, сальдо счетов и что не разнесено по сделкам' },
      { kind: 'crm', to: '/prices', section: 'prices', label: 'Прайсы', hint: 'Найти позицию и цену, увидеть пометки о качестве данных' },
      { kind: 'crm', to: '/quote', section: 'quote', label: 'Конструктор КП', hint: 'Собрать предложение, посчитать итог и напечатать бланк' },
      { kind: 'crm', to: '/estimate', section: 'estimate', label: 'Проценка установки', hint: 'Смета монтажа по нормативам: каталог, расчёт ядром, сохранённые сметы' },
      { kind: 'crm', to: '/purchasing', section: 'purchasing', label: 'Кабинет закупщика', hint: 'Заявки на оплату поставщикам: что согласовано, что просрочено' },
      { kind: 'crm', to: '/smart/invoices', section: 'invoices', label: 'Счета клиенту', hint: 'Какие счета мы выставили' },
      {
        kind: 'crm',
        to: '/smart/payment-calendar',
        section: 'payment-calendar',
        label: 'Счета на оплату',
        hint: 'Что мы должны заплатить поставщикам (платёжный календарь)',
      },
    ],
  },
  {
    id: 'docs',
    title: 'Документы',
    items: [
      { kind: 'crm', to: '/deal-docs', section: 'deal-docs', label: 'Документы по сделке', hint: 'Счета клиенту и поставщику, договоры, отчёты — все виды сразу, с 2025 года' },
      { kind: 'crm', to: '/print-forms', section: 'print-forms', label: 'Печатные формы', hint: 'Счёт, договор-счёт, КП и акт из своей базы — вместо печати Bitrix' },
      { kind: 'crm', to: '/smart/contracts', section: 'contracts', label: 'Договоры', hint: 'Реестр договоров' },
      { kind: 'crm', to: '/smart/avr', section: 'avr', label: 'АВР', hint: 'Реестр актов выполненных работ' },
      { kind: 'crm', to: '/smart/deal-reports', section: 'deal-reports', label: 'Отчёты по сделкам', hint: 'Результат согласований' },
    ],
  },
  {
    // Инженерный блок портала 73: 25 485 строк в ядре на 28.07.2026, разделы работают.
    // Фотографий здесь нет — в ядро переносятся только ключи, файлы раздаёт портал.
    id: 'engineering',
    title: 'Инженерия',
    items: [
      { kind: 'crm', to: '/my-profile', section: 'my-profile', label: 'Личный кабинет', hint: 'Ваши баллы, бейджи, выплаты и работы — только ваши' },
      { kind: 'crm', to: '/verifications', section: 'verifications', label: 'Верификации судов' },
      { kind: 'crm', to: '/job-reports', section: 'job-reports', label: 'Отчёты по заявкам' },
      { kind: 'crm', to: '/equip-acts', section: 'equip-acts', label: 'Акты на оборудование' },
      { kind: 'crm', to: '/auto-verifications', section: 'verifications', label: 'Автоверификации', hint: 'Проверки автоматики и электрики: эта форма в Bitrix не уходит вовсе' },
      { kind: 'crm', to: '/equip-due', section: 'equip-acts', label: 'Батарейки и сроки', hint: 'Что и когда пора менять на судах — по последним актам на оборудование' },
      { kind: 'crm', to: '/engineer-schedule', section: 'engineer-schedule', label: 'Календарь инженеров', hint: 'Кто на каком судне и в какие дни; отпуска, больничные и командировки учтены' },
      { kind: 'crm', to: '/calendar', section: 'calendar', label: 'Календарь', hint: 'Встречи и события из календаря Bitrix: день и неделя по людям. Частные — только время и занятость; отпуска — в «Отсутствиях»' },
      { kind: 'crm', to: '/radio-ref', section: 'radio-ref', label: 'Справочник радиоинженера', hint: 'Методичка: нормативка ГМССБ, АКБ, ЭКНИС, РЛС, инструкции по монтажу — за логином, а не как на портале' },
      { kind: 'crm', to: '/absence', section: 'absence', label: 'Отсутствия', hint: 'Кто и когда в отпуске, на больничном, в командировке или в отгуле; заявки кадров и заявки на командировку. Денег командировки и причины отсутствия своими словами здесь нет' },
      { kind: 'crm', to: '/gratitude', section: 'gratitude', label: 'Благодарности', hint: 'Кого отметил заказчик и какие значки у человека на портале 73. Сами письма в зеркало не переносятся — видно только след начисления' },
      { kind: 'crm', to: '/fleet', section: 'fleet', label: 'Наш флот и владельцы', hint: 'Чьи это суда и как связаться с владельцем; чего в реестре РМРС есть, а у нас нет' },
      { kind: 'crm', to: '/avr-parsed', section: 'avr-parsed', label: 'Разборы актов АВР', hint: 'Что делали по акту: состав работ, поставки, компонентный ремонт, позиции счёта. Часов и исполнителей здесь нет — решение о их видимости за владельцем' },
    ],
  },
  {
    id: 'rs',
    title: 'Реестр РМРС',
    items: [
      { kind: 'crm', to: '/rs/vessels', section: 'rs-vessels', label: 'Суда в реестре', hint: 'Есть ли судно в реестре Регистра и что мы на нём делали' },
      { kind: 'crm', to: '/rs/products', section: 'rs-products', label: 'Одобренные изделия', hint: 'Свидетельства Регистра: до какой даты действует' },
      { kind: 'crm', to: '/rs/suppliers', section: 'rs-suppliers', label: 'Признанные сервисники', hint: 'Кто и что вправе обслуживать, по городам' },
      { kind: 'crm', to: '/certs', section: 'certs', label: 'Сертификаты РМРС/РКО', hint: 'Наши свидетельства на оборудование: что и до какой даты действует' },
    ],
  },
  {
    id: 'stock',
    title: 'Склад и 1С',
    items: [
      { kind: 'crm', to: '/stock', section: 'stock', label: 'Склад', hint: 'Есть ли позиция, сколько свободно, на каком складе' },
      { kind: 'crm', to: '/shipping', section: 'shipping', label: 'Отгрузка', hint: 'Что отгружено и что ждёт отгрузки, по данным 1С' },
      { kind: 'crm', to: '/nelikvid', section: 'nelikvid', label: 'Неликвиды', hint: 'Что лежит на складах и не двигается — те же цифры, что в кабинете руководителя' },
    ],
  },
  {
    id: 'boss',
    title: 'Руководителю',
    items: [
      { kind: 'crm', to: '/analytics', section: 'analytics', label: 'Аналитика продаж', hint: 'Выручка и сделки по месяцам, воронкам и отделам' },
      { kind: 'crm', to: '/sales-rating', section: 'sales-rating', label: 'Рейтинг ОП', hint: 'Кто сколько закрыл за период' },
      { kind: 'crm', to: '/money', section: 'money', label: 'Касса и ФОТ', hint: 'Наличные регионов и выплаты людям — видно только владельцу' },
      { kind: 'crm', to: '/admin/users', section: 'admin-users', label: 'Учётки и доступ', hint: 'Роли, наборы, блокировки и выдача паролей учёток CRM — видно только владельцу' },
      { kind: 'crm', to: '/bpe-defs', section: 'bpe-defs', label: 'Конструктор процессов', hint: 'Создание и правка процессов движка: триггер, условия, шаги — данными, без программиста. Видно только владельцу' },
      { kind: 'crm', to: '/overtime', section: 'overtime', label: 'Овертаймы и командировки', hint: 'Переработки, суточные и каршеринг инженеров — видно только владельцу' },
      { kind: 'crm', to: '/carsharing', section: 'carsharing', label: 'Каршеринг', hint: 'Служебный транспорт: где машина, сколько намотала, кто не закрыл поездку. Номера и адреса личных авто закрыты правилом' },
      { kind: 'crm', to: '/engineer-ledger', section: 'engineer-ledger', label: 'Ведомость инженера', hint: 'Баллы, бейджи и выплаты инженеров портала 73: кому сколько начислено и сколько должны — видно только владельцу' },
      { kind: 'crm', to: '/initiatives', section: 'initiatives', label: 'Инициативы инженеров', hint: 'Кому и сколько причитается за инициативу (10 % чистой прибыли) и где вознаграждение сгорит из-за оформления — видно только владельцу' },
      { kind: 'crm', to: '/coord-projects', section: 'coord-projects', label: 'Проекты координатора', hint: 'Карточки проектов по сделкам с брифинга: чек-лист, вехи, журнал. Денег проекта и дневника работ здесь нет — их не переносит зеркало' },
      { kind: 'crm', to: '/reports', section: 'reports', label: 'Отчёты руководителя', hint: 'Переносы срока оплаты, динамика долга, возраст ошибок' },
      { kind: 'crm', to: '/xreports', section: 'xreports', label: 'Сквозные отчёты', hint: 'Что происходит между учётами: деньги без разноски, оплаты без документов, работы без счёта' },
      { kind: 'crm', to: '/deal-links', section: 'deal-links', label: 'Связки сделок', hint: 'Сделка ↔ смарт-процессы ↔ работы портала 73: что привязано, где ссылки ведут в никуда и какая доля сделок покрыта' },
      { kind: 'crm', to: '/bx-smart', section: 'bx-smart', label: 'Смарт-процессы Bitrix', hint: 'Двадцать типов, дозеркаленных по своду аудита: реестр УПД, стадии отгрузки, транзакции, возврат СЗЧ и другие. Только чтение зеркала' },
      { kind: 'crm', to: '/bx-lists', section: 'bx-lists', label: 'Списки Bitrix', hint: 'Реестр универсальных списков Bitrix и их элементы с 01.01.2025: верификации, отпуска, счета БП. Кадровые списки помечены бейджем; только чтение' },
      { kind: 'crm', to: '/data-quality', section: 'data-quality', label: 'Качество данных', hint: 'Чему в зеркале нельзя верить: что чинить, где чинится и растёт ли это' },
      // Кода раздела у кабинета нет намеренно: он собирает данные из «Сделок» и «Дебиторки»,
      // и права на них проверяются там же. Своего кода в rights.РАЗДЕЛЫ у него не существует.
      // (Комментарий стоял двумя пунктами выше, над «Кассой и ФОТ», у которой код как раз есть.)
      // «Кабинет руководителя», а не «Кабинет»: без уточнения его путают с личным
      // кабинетом. Это же самоназвание стоит в тексте самого экрана и в подсказках
      // соседних разделов, поэтому правим меню, а не заголовок.
      { kind: 'crm', to: '/dashboard', label: 'Кабинет руководителя', hint: 'Деньги, люди и сроки по своей зоне' },
      { kind: 'crm', to: '/health', section: 'health', label: 'Состояние ядра', hint: 'Можно ли доверять данным прямо сейчас' },
    ],
  },
  {
    id: 'portals',
    title: 'Пока на портале',
    note: 'Разделов ещё нет в CRM. Открываются в новой вкладке на старом портале, '
      + 'вход туда отдельный. Данные там свои — в CRM они не попадают.',
    items: [
      // Порталы 55, 73, exec и buh — приложения с навигацией по хэшу: раздел
      // открывается адресом вида «/#/analytics», отдельных путей у них нет.
      {
        kind: 'portal', portal: 'портал 55', label: 'Трекер поставок',
        href: 'https://55.cirspb.com/#/supply',
      },
      {
        kind: 'portal', portal: 'портал 73', label: 'Верификации',
        href: 'https://73.cirspb.com/#/verifications',
      },
      {
        // Чат общий на три портала (D1 cir-chat) и в CRM НЕ ЗЕРКАЛИТСЯ. Это решение по
        // данным, а не «пока не дошли руки»: половина живой переписки — личные ЛС между
        // двумя людьми, а связи «обсуждали вот эту сделку» в данных нет вовсе — сообщений
        // с номером сделки, написанных человеком, ноль (замер 28.07.2026). Запрет уже
        // стоит в коде: 'messages', 'channels', 'channel_access' перечислены в
        // ЗАПРЕЩЁННЫЕ_ТАБЛИЦЫ синка инициатив, и проверка отбора падает на старте, если
        // их вписать. Разбор целиком — в задаче про чат.
        kind: 'portal', portal: 'портал 73', label: 'Чаты',
        href: 'https://73.cirspb.com/chat.html',
        hint: 'Общий чат СиР — тот же, что на брифинге; переписка в CRM не попадает',
      },
      {
        kind: 'portal', portal: 'кабинет руководителя', label: 'Моя команда',
        href: 'https://exec.cirspb.com/#/team',
      },
      {
        // У бухгалтерского портала навигации нет вовсе: одна страница, разделы
        // переключаются кнопками на ней. Прямой ссылки на «Банк» не существует,
        // поэтому ведём на портал и говорим, что нажать.
        kind: 'portal', portal: 'buh', label: 'Банк',
        href: 'https://buh.cirspb.com/',
        hint: 'Откроется «Стол» — раздел «Банк» переключается кнопкой на самой странице',
      },
    ],
  },
]

/**
 * Пункты, которые реально показываем: без разделов без маршрута и без закрытых правами.
 *
 * `denied` — предикат из прав (`useRightsApi().denied`). Он ОБЯЗАН отвечать false, пока
 * права не пришли: иначе на первой отрисовке меню схлопнулось бы до пустого и человек
 * решил бы, что CRM сломалась. Пункт без кода раздела права не касаются вовсе.
 */
export const visibleItems = (g: NavGroup, denied: (code?: string) => boolean = () => false): NavItem[] =>
  g.items.filter((i) => i.kind !== 'crm' || (!i.pending && !denied(i.section)))

/** Активен ли раздел: сам путь или его карточка (/deals/123). */
export const isCurrent = (pathname: string, to: string): boolean =>
  pathname === to || pathname.startsWith(`${to}/`)

/**
 * Код раздела, открытого сейчас. Нужен, чтобы показать оговорки прав именно про него
 * (`sections[].partial` из /api/me), а не все подряд.
 *
 * Ищем самое длинное совпадение пути: у «/smart/invoices» и «/rs/vessels» есть общие
 * начала с другими пунктами, и первое подошедшее дало бы чужой раздел.
 */
export function sectionOfPath(pathname: string): string {
  let код = ''
  let длина = -1
  for (const g of NAV_GROUPS) {
    for (const i of g.items) {
      if (i.kind !== 'crm' || !i.section) continue
      if (isCurrent(pathname, i.to) && i.to.length > длина) {
        код = i.section
        длина = i.to.length
      }
    }
  }
  return код
}
